import React, { useState } from "react";
import { toast } from "sonner";
import { patchNoteSoap } from "@/lib/dictation";
import { unsignNote } from "@/lib/noteClaim";

const PARTS = [["subjective", "Subjective"], ["objective", "Objective"], ["assessment", "Assessment"], ["plan", "Plan"]];
const FAINT = "#9B9B97", GREY2 = "#6C6C69";

// "Subjective: …\nObjective: …" → { subjective, objective, assessment, plan }. Text before the first heading goes to Subjective.
export function splitSoap(text) {
  const out = { pre: "", subjective: "", objective: "", assessment: "", plan: "" };
  let cur = "pre";
  String(text || "").split("\n").forEach((ln) => {
    const m = /^\s*(subjective|objective|assessment(?:\s*(?:and|&)\s*plan)?|plan)\b\s*(?::\s*(.*))?$/i.exec(ln);
    if (m) { cur = /^assess/i.test(m[1]) ? "assessment" : m[1].toLowerCase(); if (m[2]) out[cur] += `${m[2]}\n`; return; }
    out[cur] += `${ln}\n`;
  });
  Object.keys(out).forEach((k) => { out[k] = out[k].trim(); });
  return out;
}
// Text before the first S/O/A/P heading (demographics, chief complaint) is kept as-is in front.
const joinSoap = (f) => [f.pre.trim(), ...PARTS.filter(([k]) => f[k].trim()).map(([k, l]) => `${l}:\n${f[k].trim()}`)].filter(Boolean).join("\n\n");

export const EditedTag = () => <span className="text-[10.5px] font-semibold px-1.5 rounded-[5px]" style={{ border: "1px solid #E9E9EC", color: FAINT }} data-testid="th-act-edited">Edited</span>;

// Activity → Edit note (visit already in Encounter review): PATCH note SOAP; signed notes unlock via Unsign first.
export function ActNoteEditor({ v, note, text, chart, onCancel, onSaved }) {
  const n = note?.data;
  const [f, setF] = useState(() => splitSoap(text));
  const [showPre] = useState(() => !!f.pre);
  const [busy, setBusy] = useState(false);
  const signed = n?.signed === true;
  const unsign = async () => {
    setBusy(true);
    try { await unsignNote(v.note_id); await note.refetch?.(); toast.success("Unsigned — you can edit now."); } catch { toast.error("Couldn't unsign this note."); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { const t = joinSoap(f); await patchNoteSoap(v.note_id, { soap_text: t }); toast.success("Note saved"); onSaved(t); } catch (e) { toast.error(e?.message || "Couldn't save the note."); } finally { setBusy(false); }
  };
  const when = n?.signed_at ? new Date(n.signed_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
  return (
    <div className="th-card" data-testid={`th-act-editor-${v.visit_id}`}>
      <div className="th-lab mb-2">Lumina · SOAP note</div>
      {signed ? <div className="text-[12.5px] mb-2" style={{ color: GREY2 }} data-testid={`th-act-signed-${v.visit_id}`}>✓ Signed by {n.signed_by?.name || n.signed_by?.email || "—"}{when ? ` · ${when}` : ""}. Unsign to edit it.</div> : null}
      {(showPre ? [["pre", "Note opening (before Subjective)"], ...PARTS] : PARTS).map(([k, l]) => (
        <label key={k} className="block mb-2">
          <span className="block text-[11px] font-bold uppercase tracking-[.05em] mb-1" style={{ color: FAINT }}>{l}</span>
          <textarea value={f[k]} readOnly={signed} onChange={(e) => setF({ ...f, [k]: e.target.value })} rows={k === "pre" ? 6 : k === "plan" || k === "subjective" ? 4 : 3}
            className="w-full rounded-[10px] border px-3 py-2 text-[13px] outline-none focus:border-[#5856D6] resize-y" style={{ borderColor: "#E9E9EC", background: signed ? "#FBFBFD" : "#fff" }} data-testid={`th-act-edit-${k}-${v.visit_id}`} />
        </label>
      ))}
      <div className="flex items-center gap-2 mt-1">
        {chart ? <span className="text-[12px] font-semibold" style={{ color: FAINT, cursor: "not-allowed" }} title="Coming soon" data-testid={`th-act-edit-chart-${v.visit_id}`}>Edit in full chart ↗</span> : null}
        <span className="flex-1" />
        <button type="button" className="th-btn sec" onClick={onCancel} disabled={busy} data-testid={`th-act-edit-cancel-${v.visit_id}`}>Cancel</button>
        {signed ? <button type="button" className="th-btn ink" onClick={unsign} disabled={busy} data-testid={`th-act-unsign-${v.visit_id}`}>{busy ? "Unsigning…" : "Unsign"}</button>
          : <button type="button" className="th-btn ink" onClick={save} disabled={busy} data-testid={`th-act-edit-save-${v.visit_id}`}>{busy ? "Saving…" : "Save note"}</button>}
      </div>
    </div>
  );
}
